import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Upload, Download, Loader2, CheckCircle, AlertCircle,
  FileSpreadsheet, Tags, Sparkles, ArrowRight, Plus,
  AlertTriangle, Trash2, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const CategoryClassifierTab = ({ token }) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [sensitiveCategories, setSensitiveCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSensitiveCategoryName, setNewSensitiveCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (token) {
      fetchCategories();
      fetchSensitiveCategories();
      fetchJobs();
    }
  }, [token]);

  useEffect(() => {
    let interval;
    if (polling) {
      interval = setInterval(fetchStatus, 2000);
    }
    return () => clearInterval(interval);
  }, [polling]);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(`${API}/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSensitiveCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/sensitive-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSensitiveCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching sensitive categories:', err);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/category-classifier/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
      if (!response.data.in_progress && polling) {
        setPolling(false);
        fetchJobs();
        if (response.data.processed > 0) {
          toast.success(`Clasificación completada: ${response.data.processed} procesados`);
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/admin/category-classifier/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.jobs || []);
      setStatus(response.data.current_status || null);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    toast.info('Las categorías se crean automáticamente al importar productos con esa categoría');
    setNewCategoryName('');
  };

  const handleAddSensitiveCategory = async () => {
    if (!newSensitiveCategoryName.trim()) return;
    setAddingCategory(true);
    try {
      await axios.post(`${API}/admin/sensitive-categories`, 
        { category: newSensitiveCategoryName.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewSensitiveCategoryName('');
      fetchSensitiveCategories();
      toast.success('Categoría sensible añadida');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir categoría sensible');
    } finally {
      setAddingCategory(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError('');
    }
  };

  const handleProcess = async () => {
    if (!file) {
      setError('Selecciona un archivo primero');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${API}/admin/category-classifier/process`, formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`Clasificación iniciada: ${response.data.total_rows} filas (${response.data.discarded_no_website} descartadas sin web)`);
      setPolling(true);
      setFile(null);
      setFileName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (jobId, filename) => {
    try {
      const response = await axios.get(
        `${API}/admin/category-classifier/download/${jobId}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'categorizado.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Descarga completada');
    } catch (err) {
      toast.error('Error al descargar');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!confirm('¿Eliminar este resultado?')) return;
    try {
      await axios.delete(`${API}/admin/category-classifier/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eliminado');
      fetchJobs();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const clearFile = () => {
    setFile(null);
    setFileName('');
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const progressPercent = status?.total > 0
    ? Math.round((status.processed / status.total) * 100)
    : 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Categories Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5 text-purple-600" />
            Categorías Disponibles ({categories.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input placeholder="Nueva categoría..." value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value.toUpperCase())} className="max-w-xs"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); }} />
            <Button onClick={handleAddCategory} variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-1" /> Añadir
            </Button>
          </div>
          {loadingCategories ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
            </div>
          ) : categories.length > 0 ? (
            <div className="flex flex-wrap gap-1 max-h-48 overflow-y-auto p-2 bg-slate-50 rounded-lg">
              {categories.map((cat, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">{cat}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No hay categorías</p>
          )}
        </CardContent>
      </Card>

      {/* Sensitive Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Categorías Sensibles ({sensitiveCategories.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input placeholder="Nueva categoría sensible..." value={newSensitiveCategoryName}
              onChange={(e) => setNewSensitiveCategoryName(e.target.value.toUpperCase())} className="max-w-xs"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddSensitiveCategory(); }} />
            <Button onClick={handleAddSensitiveCategory} variant="outline" size="sm" disabled={addingCategory}
              className="border-amber-500 text-amber-600 hover:bg-amber-50">
              {addingCategory ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Añadir
            </Button>
          </div>
          {sensitiveCategories.length > 0 ? (
            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-2 bg-amber-50 rounded-lg">
              {sensitiveCategories.map((cat, idx) => (
                <Badge key={idx} variant="destructive" className="text-xs bg-amber-100 text-amber-800">{cat}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No hay categorías sensibles</p>
          )}
        </CardContent>
      </Card>

      {/* Classifier */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" />
            Clasificador de Categorías (IA)
          </CardTitle>
          <CardDescription>
            Sube Excel con dominios web. La IA asigna MÚLTIPLES categorías afines separadas por comas. Filas sin website se descartan.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Upload */}
          <div className="space-y-4">
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".xlsx,.xls,.csv" className="hidden" />
            {!fileName ? (
              <div onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50/50 transition-colors">
                <FileSpreadsheet className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                <p className="text-slate-600 font-medium">Haz clic para seleccionar un archivo</p>
                <p className="text-sm text-slate-400 mt-1">.xlsx, .xls o .csv</p>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-slate-100 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-8 w-8 text-green-600" />
                  <p className="font-medium text-slate-700">{fileName}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={clearFile}>Cambiar</Button>
              </div>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button onClick={handleProcess}
            disabled={!file || uploading || status?.in_progress}
            className="w-full bg-purple-600 hover:bg-purple-700"
            data-testid="classify-btn">
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Subiendo...</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Clasificar Categorías</>
            )}
          </Button>

          {/* Progress */}
          {status?.in_progress && (
            <div className="space-y-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-purple-700 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Clasificando...
                </span>
                <span className="text-sm text-purple-600">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
              <div className="flex justify-between text-xs text-purple-600">
                <span>Procesados: {status.processed}</span>
                <span>Errores: {status.errors}</span>
                <span>Total: {status.total}</span>
              </div>
            </div>
          )}

          {/* Refresh */}
          <Button variant="outline" size="sm" onClick={() => { fetchStatus(); fetchJobs(); }}>
            <RefreshCw className="h-4 w-4 mr-2" /> Actualizar estado
          </Button>
        </CardContent>
      </Card>

      {/* Jobs History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-green-600" />
            Resultados de Clasificaciones
          </CardTitle>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No hay clasificaciones guardadas</p>
          ) : (
            <div className="space-y-3">
              {jobs.map((job) => {
                const filename = job.original_filename || 'clasificado.xlsx';
                const baseName = filename.includes('.') ? filename.split('.').slice(0, -1).join('.') : filename;
                return (
                  <div key={job.job_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{filename}</p>
                      <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                        <span>{formatDate(job.created_at)}</span>
                        {job.stats && (
                          <>
                            <span className="text-green-600">{job.stats.processed} procesados</span>
                            <span className="text-red-600">{job.stats.errors} errores</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-3 shrink-0">
                      <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleDownload(job.job_id, `${baseName}_categorizado.xlsx`)}>
                        <Download className="h-4 w-4 mr-1" /> Descargar
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteJob(job.job_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CategoryClassifierTab;
